import {
  COUNTRY_TO_CURRENCY,
  CurrencyCode,
  CURRENCY_STORAGE_KEY,
  DEFAULT_CURRENCY,
  EXCHANGE_RATES_STORAGE_KEY,
  EXCHANGE_RATES_TTL_MS,
  SUPPORTED_CURRENCIES,
  TIMEZONE_TO_CURRENCY,
} from "@/lib/config/currency";
import { getNumberLocale } from "@/lib/i18n/formatLocale";

export interface ExchangeRatesSnapshot {
  base: "MAD";
  rates: Record<CurrencyCode, number>;
  fetchedAt: number;
}

const FALLBACK_RATES: Record<CurrencyCode, number> = {
  MAD: 1,
  EUR: 0.092,
  USD: 0.099,
  CAD: 0.135,
  GBP: 0.078,
  AED: 0.363,
};

const isCurrencyCode = (value: string): value is CurrencyCode => {
  return SUPPORTED_CURRENCIES.includes(value as CurrencyCode);
};

const getCachedSnapshot = (): ExchangeRatesSnapshot | null => {
  if (typeof window === "undefined") return null;
  const raw = localStorage.getItem(EXCHANGE_RATES_STORAGE_KEY);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as ExchangeRatesSnapshot;
    if (!parsed?.fetchedAt || !parsed?.rates) return null;
    return parsed;
  } catch {
    return null;
  }
};

const setCachedSnapshot = (snapshot: ExchangeRatesSnapshot): void => {
  if (typeof window === "undefined") return;
  localStorage.setItem(EXCHANGE_RATES_STORAGE_KEY, JSON.stringify(snapshot));
};

const buildSnapshotFromApi = (apiRates: Record<string, number>): ExchangeRatesSnapshot => {
  const rates = { ...FALLBACK_RATES };
  for (const code of SUPPORTED_CURRENCIES) {
    if (code === "MAD") continue;
    const rate = apiRates[code];
    if (typeof rate === "number" && Number.isFinite(rate) && rate > 0) {
      rates[code] = rate;
    }
  }

  return {
    base: "MAD",
    rates,
    fetchedAt: Date.now(),
  };
};

const fetchLiveRates = async (): Promise<ExchangeRatesSnapshot> => {
  const response = await fetch("https://open.er-api.com/v6/latest/MAD", {
    method: "GET",
    headers: { Accept: "application/json" },
  });

  if (!response.ok) {
    throw new Error(`Impossible de récupérer les taux (${response.status})`);
  }

  const payload = await response.json() as { rates?: Record<string, number> };
  return buildSnapshotFromApi(payload?.rates ?? {});
};

export const getExchangeRates = async (forceRefresh = false): Promise<ExchangeRatesSnapshot> => {
  const cached = getCachedSnapshot();
  const isFresh = cached && Date.now() - cached.fetchedAt < EXCHANGE_RATES_TTL_MS;

  if (!forceRefresh && isFresh) {
    return cached;
  }

  try {
    const live = await fetchLiveRates();
    setCachedSnapshot(live);
    return live;
  } catch {
    if (cached) return cached;
    const fallback: ExchangeRatesSnapshot = {
      base: "MAD",
      rates: { ...FALLBACK_RATES },
      fetchedAt: Date.now(),
    };
    setCachedSnapshot(fallback);
    return fallback;
  }
};

export const convertPrice = (
  amountMad: number,
  currency: CurrencyCode,
  rates?: ExchangeRatesSnapshot["rates"]
): number => {
  if (currency === "MAD") return amountMad;
  const rate = rates?.[currency] ?? FALLBACK_RATES[currency];
  return amountMad * rate;
};

export const formatPrice = (
  amount: number,
  currency: CurrencyCode,
  lang = "fr",
  options?: { minimumFractionDigits?: number; maximumFractionDigits?: number }
): string => {
  const locale = getNumberLocale(lang);
  const min = options?.minimumFractionDigits ?? (currency === "MAD" ? 0 : 0);
  const max = options?.maximumFractionDigits ?? (currency === "MAD" ? 0 : 0);

  if (currency === "MAD") {
    const value = new Intl.NumberFormat(locale, {
      minimumFractionDigits: min,
      maximumFractionDigits: max,
    }).format(amount);
    return `${value} MAD`;
  }

  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    currencyDisplay: "narrowSymbol",
    minimumFractionDigits: min,
    maximumFractionDigits: max,
  }).format(amount);
};

export const formatPriceFromMad = (
  amountMad: number,
  currency: CurrencyCode,
  lang = "fr",
  rates?: ExchangeRatesSnapshot["rates"],
  options?: { minimumFractionDigits?: number; maximumFractionDigits?: number }
): string => {
  const converted = convertPrice(amountMad, currency, rates);
  return formatPrice(converted, currency, lang, options);
};

export const getStoredCurrency = (): CurrencyCode => {
  if (typeof window === "undefined") return DEFAULT_CURRENCY;
  const raw = localStorage.getItem(CURRENCY_STORAGE_KEY);
  if (!raw || !isCurrencyCode(raw)) return DEFAULT_CURRENCY;
  return raw;
};

export const persistCurrency = (currency: CurrencyCode): void => {
  if (typeof window === "undefined") return;
  localStorage.setItem(CURRENCY_STORAGE_KEY, currency);
};

export const detectCurrencyFromUserEnvironment = (): CurrencyCode => {
  if (typeof window === "undefined") return DEFAULT_CURRENCY;

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (timezone && TIMEZONE_TO_CURRENCY[timezone]) {
    return TIMEZONE_TO_CURRENCY[timezone];
  }

  const locale = navigator.language || "";
  const country = locale.split("-")[1]?.toUpperCase();
  if (country && COUNTRY_TO_CURRENCY[country]) {
    return COUNTRY_TO_CURRENCY[country];
  }

  return DEFAULT_CURRENCY;
};

export const currencyService = {
  getExchangeRates,
  convertPrice,
  formatPrice,
  formatPriceFromMad,
  getStoredCurrency,
  persistCurrency,
  detectCurrencyFromUserEnvironment,
};
